import { IonContent } from '@ionic/react';
import React from 'react';
import { useForm } from 'react-hook-form';

import { InfoProtection } from '@/components/InfoProtection';
import { UiTypography } from '@/components/ui/Typography';
import { useAccount } from '@/hooks/useAccount';
import { useUpdateStripeClient } from '@/hooks/useUpdateStripeClient';
import { webCampaign } from '@/modules/webCampaign';
import { useLayoutSelections } from '@/pages/Onboarding/hooks/useLayoutSelections';
import { checkIsValidateEmail } from '@/pages/Onboarding/utils/checkIsValidEmail';

import { EmailButton } from './components/Button';
import { ConnectUs } from './components/ConnectUs';
import { DownloadLinks } from './components/DownloadLinks';
import { EmailInput } from './components/Input';
import {
  MainBlockContentStyles,
  MainBlockPhraseStyles,
  MainBlockStyles,
  SuccessPageWithEmailStyles,
} from './WithEmail.styles';

const WithEmailVariant: React.FC = () => {
  const { user, refreshUser } = useAccount();

  const { updateStripeClient } = useUpdateStripeClient();

  const { handleSubmit, register, formState } = useForm<{
    email: string;
  }>({
    defaultValues: {
      email: user?.email || '',
    },
  });

  const onSubmit = (data: { email: string }) => {
    updateStripeClient(data.email).then(() => {
      refreshUser();
    });
  };

  const { ageGeneration, bodyShape, hairColor } = useLayoutSelections();

  const hasEmail = !!user?.email;

  const disableForm =
    formState.isSubmitting || formState.isSubmitSuccessful || hasEmail;

  return (
    <SuccessPageWithEmailStyles background={webCampaign.getStaticTheme()}>
      <IonContent>
        <UiTypography
          className="success-payment-with-email-title"
          component="h3"
          align="center"
        >
          Congrats, your purchase
          <br /> was successful!
        </UiTypography>
        <MainBlockStyles>
          <img
            src={`https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/success-page/with-email/${ageGeneration}_${bodyShape}_${hairColor}.png`}
            alt="AI"
          />
          {hasEmail ? (
            <MainBlockPhraseStyles variant="title-large" align="center">
              <img
                src="https://d3hhlnlnie19wm.cloudfront.net/web-onb/assets/paywall/cant-wait-phrase.png"
                alt="cant wait"
              />
            </MainBlockPhraseStyles>
          ) : (
            <MainBlockContentStyles>
              <UiTypography variant="title-large" align="center">
                Enter your email to complete the registration
              </UiTypography>
            </MainBlockContentStyles>
          )}
        </MainBlockStyles>

        {hasEmail ? (
          <div className="content">
            <UiTypography variant="body-large" align="center">
              Download Dialogue app from your store
            </UiTypography>
            <DownloadLinks />
            <UiTypography variant="body-large" align="center">
              OR via the link we sent to your email, {user.email} (check Inbox
              and Spam folders).
            </UiTypography>
            <ConnectUs />
          </div>
        ) : (
          <div className="content">
            <form onSubmit={handleSubmit(onSubmit)}>
              <EmailInput
                placeholder="email"
                type="text"
                error={formState.errors.email}
                {...register('email', {
                  validate: (value) => {
                    if (!value) {
                      return 'This field is required';
                    }

                    if (!checkIsValidateEmail(value)) {
                      return 'Invalid email address';
                    }

                    return true;
                  },
                })}
                disabled={disableForm}
              />
              <EmailButton type="submit" disabled={disableForm}>
                Complete
              </EmailButton>
            </form>
            <InfoProtection className="info-protection" />
          </div>
        )}
      </IonContent>
    </SuccessPageWithEmailStyles>
  );
};

export default WithEmailVariant;
